import { ComponentRef, ViewContainerRef } from "@angular/core";
import { ConnectedPosition, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";

import { MentionCandidate } from "@mypxplat/xplat/core";

import { MentionPickerComponent } from "./mention-picker.component";

/**
 * Flip and slide order. The picker prefers to sit just under the caret, but will
 * go above it near the bottom of the viewport and switch its horizontal anchor
 * near the right edge. `withPush` then nudges whatever is left back into view,
 * so there is no combination of scroll position and window size that can leave
 * it clipped or off-screen.
 */
const POSITIONS: ConnectedPosition[] = [
  { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 4 },
  { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -4 },
  { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top", offsetY: 4 },
  { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom", offsetY: -4 },
];

export interface CaretAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Owns the floating mention picker for one input.
 *
 * The picker used to be an in-flow `<div>` rendered after the input, which is
 * why it worked under the community composer and broke everywhere else: the
 * workspace and file-detail chat inputs are docked to the bottom of an
 * `overflow: hidden` container, so the list was clipped or pushed off-screen.
 * Attaching to the body via a CDK overlay and anchoring to the caret removes
 * both problems, and means there is only one picker to style and maintain.
 */
export class MentionOverlayController {
  private _overlayRef: OverlayRef | undefined;
  private _componentRef: ComponentRef<MentionPickerComponent> | undefined;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private idPrefix: string,
    private onPicked: (candidate: MentionCandidate) => void
  ) {}

  get isOpen(): boolean {
    return !!this._overlayRef;
  }

  /** Opens the picker, or updates it in place if it is already open. */
  show(anchor: CaretAnchor | null, candidates: MentionCandidate[]) {
    if (!anchor || !candidates || !candidates.length) return this.close();

    if (!this._overlayRef) this._create(anchor);
    else this._reposition(anchor);

    this._componentRef.instance.candidates = candidates;
    this._componentRef.instance.idPrefix = this.idPrefix;
    this._componentRef.changeDetectorRef.detectChanges();
    // After the rows have rendered, so the highlighted one can be measured.
    this._componentRef.instance.scrollSelectedIntoView();
  }

  close() {
    if (this._componentRef) {
      this._componentRef.destroy();
      this._componentRef = undefined;
    }
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = undefined;
    }
  }

  /** DOM id of the highlighted row, for aria-activedescendant on the editor. */
  activeOptionId(candidates: MentionCandidate[]): string | null {
    if (!this._overlayRef || !candidates) return null;
    const index = candidates.findIndex((candidate) => candidate.selected);
    return index > -1 ? this.idPrefix + "-option-" + index : null;
  }

  private _create(anchor: CaretAnchor) {
    this._overlayRef = this.overlay.create({
      positionStrategy: this._strategy(anchor),
      // Follow the caret when the thread behind it scrolls, and give up if it
      // scrolls out of sight entirely.
      scrollStrategy: this.overlay.scrollStrategies.reposition({ autoClose: true }),
      hasBackdrop: false,
      panelClass: "myp-mention-overlay",
    });

    this._componentRef = this._overlayRef.attach(new ComponentPortal(MentionPickerComponent, this.viewContainerRef));
    this._componentRef.instance.picked.subscribe((candidate: MentionCandidate) => this.onPicked(candidate));
  }

  private _reposition(anchor: CaretAnchor) {
    this._overlayRef.updatePositionStrategy(this._strategy(anchor));
    this._overlayRef.updatePosition();
  }

  private _strategy(anchor: CaretAnchor) {
    return this.overlay.position().flexibleConnectedTo(anchor).withFlexibleDimensions(false).withPush(true).withViewportMargin(8).withPositions(POSITIONS);
  }
}

/**
 * Viewport rect of the collapsed caret, used as the overlay's virtual origin.
 *
 * Anchoring to the caret rather than to the input matters on the community
 * composer, where the box is tall enough that a list pinned to its bottom edge
 * can be a long way from what the user is typing.
 */
export function caretAnchor(fallbackEl?: HTMLElement): CaretAnchor | null {
  // Wrapped because measuring the caret must never be able to break a keystroke.
  // Range.getClientRects is not implemented everywhere (jsdom, for one), and a
  // detached or mid-mutation range can throw outright.
  try {
    const selection = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;

    if (selection && selection.rangeCount) {
      const range = selection.getRangeAt(0).cloneRange();
      range.collapse(true);

      if (typeof range.getClientRects === "function") {
        let rect = range.getClientRects()[0];

        // A collapsed range sitting exactly at a text-node boundary reports no
        // rects at all. Expanding back over one character gives a real box.
        if (!rect && range.startContainer && range.startContainer.nodeType === 3 && range.startOffset > 0) {
          const probe = range.cloneRange();
          probe.setStart(range.startContainer, range.startOffset - 1);
          const rects = probe.getClientRects();
          rect = rects[rects.length - 1];
        }

        if (rect && (rect.height || rect.width)) {
          return { x: rect.left, y: rect.top, width: 0, height: rect.height };
        }
      }
    }
  } catch (err) {
    // Fall through to the input-relative anchor.
  }

  // Empty editor, or a browser that gave us nothing: fall back to the input.
  if (fallbackEl && typeof fallbackEl.getBoundingClientRect === "function") {
    const rect = fallbackEl.getBoundingClientRect();
    return { x: rect.left, y: rect.top, width: 0, height: rect.height };
  }

  return null;
}
